<script setup lang="ts">
const WrContactFormPopup = defineAsyncComponent(
  () => import("~/components/molecules/WrContactFormPopup.vue"),
);
const Card = defineAsyncComponent(() => import("primevue/card"));

interface Product {
  name: string;
  boldName?: string;
  description: string;
  link?: string;
  cta?: string;
}

withDefaults(
  defineProps<{
    heading?: string;
    subcopy?: string;
    products?: Product[];
  }>(),
  {
    heading: "Our AI Products",
    subcopy: "Ship on platforms we have already built, tested and battle-tested.",
    products: () => [
      {
        name: "LLM ",
        boldName: "KB",
        description:
          "The brain of your AI agents. A private knowledge base built from vectors, WIKI's and a knowledge graph — turning enterprise data into source-cited, RAG-ready answers with no hallucinations.",
        link: "https://llmkb.ai/",
        cta: "See Data in Action",
      },
      {
        name: "Vaah",
        boldName: "Agents",
        description:
          "AI agents that research, act and report. Plug them into your workflows from day one and let them handle multi-step tasks end to end — every action logged and traceable.",
        cta: "See Agents in Action",
      },
      {
        name: "Genic",
        boldName: "UI",
        description:
          "GenicUI lets AI agents seamlessly interact with your existing components. It acts as a bridge—allowing any AI agent to directly render components or interfaces in your current chat.",
        link: "https://genicui.vaah.ai/",
        cta: "See Interactive Demo",
      },
    ],
  },
);

const visible = ref(false);

const openDemo = () => {
  visible.value = true;
};
</script>

<template>
  <section class="lg:mt-28 mt-10 relative">
    <h2 class="text-center ">
      <WrGradientText :text="heading" append-class="pb-4" />
    </h2>
    <p class="text-lg text-center max-w-4xl mx-auto tracking-[0.5px] lg:px-4 mb-16">
        {{ subcopy }}
    </p>
   <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-7 *:w-full *:h-full">
      <Reveal
        v-for="(product, index) in products"
        :key="product.name"
        :delay="index * 100"
        append-class="h-full"
      >
        <component
          :is="product.link ? 'a' : 'div'"
          :href="product.link"
          target="_blank"
          rel="noopener noreferrer"
          class="block h-full gradient-border-mask relative p-px dark:before:bg-[linear-gradient(91.95deg,_rgba(61,27,96,0.5)_4.5%,_rgba(63,22,104,0.5)_96.91%)] before:bg-[linear-gradient(91.95deg,_rgba(182,164,199,0.5)_4.5%,_rgba(148,103,192,0.5)_96.91%)]"
        >
          <Card
            unstyled
            class="p-4 md:p-7 relative dark:bg-none bg-[var(--color-5)] dark:bg-transparent text-[var(--primary)] text-sm h-full rounded-xl"
          >
            <template #title>
              <h3
                class="mb-4 text-3xl font-medium text-[var(--foreground)]"
                style="font-family: 'ITC Avant Garde Gothic', 'AvantGarde', sans-serif;"
              >
                {{ product.name
                }}<span class="font-bold">{{ product.boldName }}</span>
              </h3>
            </template>
            <template #content>
              <p class="m-0 mt-4">{{ product.description }}</p>
              <a
                href="#"
                class="mt-4 inline-block cursor-pointer text-sm font-semibold text-black dark:text-white underline"
                @click.prevent.stop="openDemo"
              >
                {{ product.cta }}
              </a>
            </template>
          </Card>
        </component>
      </Reveal>
    </div>

    <Teleport to="body">
      <WrContactFormPopup
        v-if="visible"
        :isOpen="visible"
        @close="visible = false"
        class="z-30"
      />
    </Teleport>
  </section>
</template>
